import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useToast } from '@/components/ui/use-toast';

export interface CollectionActivity {
  id: string;
  user_id: string;
  bill_id: string;
  workflow_id?: string;
  contact_id?: string;
  activity_type: 'email' | 'sms' | 'call' | 'letter' | 'notice' | 'legal';
  status: 'pending' | 'sent' | 'delivered' | 'failed' | 'responded';
  scheduled_at?: string;
  completed_at?: string;
  content: any;
  response_data?: any;
  created_at: string;
  updated_at: string;
}

export interface CreateCollectionActivityData {
  bill_id: string;
  workflow_id?: string;
  contact_id?: string;
  activity_type: 'email' | 'sms' | 'call' | 'letter' | 'notice' | 'legal';
  scheduled_at?: string;
  content: any;
}

export const useCollectionActivities = (billId?: string) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const activitiesQuery = useQuery({
    queryKey: ['collection-activities', billId, user?.id],
    queryFn: async () => {
      let query = supabase
        .from('collection_activities')
        .select('*')
        .order('created_at', { ascending: false });

      if (billId) {
        query = query.eq('bill_id', billId);
      }

      const { data, error } = await query;
      
      if (error) throw error;
      return data as CollectionActivity[];
    },
    enabled: !!user?.id,
  });

  const createActivityMutation = useMutation({
    mutationFn: async (activityData: CreateCollectionActivityData) => {
      const { data, error } = await supabase
        .from('collection_activities')
        .insert([{
          ...activityData,
          user_id: user?.id,
          status: 'pending',
        }])
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['collection-activities'] });
      toast({
        title: "Activity created",
        description: "Collection activity has been created successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to create activity",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return {
    activities: activitiesQuery.data || [],
    isLoading: activitiesQuery.isLoading,
    error: activitiesQuery.error,
    createActivity: createActivityMutation.mutate,
    isCreating: createActivityMutation.isPending,
  };
};